import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supabase } from '../lib/supabaseClient';

// Helper: Fetch the custom profile (Role & Department)
const fetchUserProfile = async (userId) => {
    const { data, error } = await supabase
        .from('profiles')
        .select(`
            full_name, 
            role, 
            department_id,
            preferences,
            avatar_url,
            departments ( name )
        `)
        .eq('id', userId)
        .single();
    
    if (error) {
        console.warn("Profile fetch warning:", error.message);
        return null; 
    }

    return {
        ...data,
      preferences: data.preferences || {},
        department_name: data.departments?.name || 'N/A', 
    };
}

// Thunk: Login
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      // 1. Authenticate with Supabase Auth
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      
      // 2. Fetch the extra Profile Data (Role)
      const profile = await fetchUserProfile(data.user.id);
      
      if (!profile) {
          return rejectWithValue("Login successful, but User Profile (Role) is missing. Please contact Admin.");
      }
      
      // 3. Merge and return
      return { ...data.user, ...profile };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Thunk: Restore Session (Page Reload)
export const loadUserSession = createAsyncThunk(
    'auth/loadUserSession',
    async (_, { rejectWithValue }) => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return { user: null, isAuthenticated: false };

            const profile = await fetchUserProfile(session.user.id);
            
            // If profile missing, we are authenticated but not authorized
            if (!profile) return { user: session.user, isAuthenticated: true, roleMissing: true };

            return { 
                user: { ...session.user, ...profile }, 
                isAuthenticated: true,
                roleMissing: false
            };
        } catch (error) {
            return rejectWithValue("Session invalid.");
        }
    }
);

// Thunk: Logout
export const logoutUser = createAsyncThunk('auth/logoutUser', async (_, { rejectWithValue }) => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    
    // Failsafe: Nuke browser storage
    sessionStorage.clear();
    localStorage.clear();
    
    return true;
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    loading: true, // Set to true by default to prevent Login flash on hard refresh
    error: null,
    isAuthenticated: false,
  },
  reducers: {
    setUser: (state, action) => {
      console.log("🧠 REDUCER (auth/setUser):", { 
        previousLoading: state.loading, 
        previousAuth: state.isAuthenticated,
        payload: action.payload 
      });
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      state.loading = false;
      console.log("🧠 REDUCER (auth/setUser) DONE: New loading is false");
    },
    setLoading: (state, action) => {
      if (action.payload === true && state.loading === true) {
        console.warn("⚠️ REDUNDANT setLoading(true) detected. This might indicate a reset loop.");
      }
      console.log("🧠 REDUCER (auth/setLoading):", { 
        previous: state.loading, 
        new: action.payload 
      });
      state.loading = action.payload;
    },
    updatePreferencesLocal: (state, action) => {
      if (state.user) {
        state.user.preferences = {
          ...state.user.preferences,
          ...action.payload
        };
      }
    },
    updateUserLocal: (state, action) => {
      if (state.user) {
        state.user = {
          ...state.user,
          ...action.payload
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Load Session
      .addCase(loadUserSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadUserSession.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = action.payload.isAuthenticated;
      })
      .addCase(loadUserSession.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
      })

      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.user = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setUser, setLoading, updatePreferencesLocal, updateUserLocal } = authSlice.actions;
export default authSlice.reducer;